import { useEffect } from 'react';

import { IconAngleRight } from '@iwoca/icons';
import { Accordion } from '@iwoca/orion';

import styles from './BankStatementUploadRevamp.module.css';
import { useGetDocuments } from '../../../../api/lendingApi/lendingApiQueries';
import { useDocumentUploadMutation } from '../../../../api/lendingApi/useDocumentUploadMutation';
import { useContextStateKey } from '../../../../hooks/useContextStateKey';
import { useFileUploadReducer } from '../../../../hooks/useFileUploadReducer';
import DragDropUpload from '../components/DragDropUpload/DragDropUpload';
import FileStatusSections from '../components/FileStatusSections/FileStatusSections';

const DOCUMENT_TYPE = 'bank statement';

const listOfInstructions = [
  <>
    You can download these from your online banking.{' '}
    <a
      href="https://static.iwoca.com/pdf/iwoca_bank_statement_pdf_download_guide.pdf"
      target="_blank"
      rel="noreferrer"
    >
      See instructions
    </a>
    .
  </>,
  'Files must be PDF or JPG, JPEG, PNG and no more than 2GB.',
  'These should be for the account(s) where the majority of your business revenue is generated.',
  'Please ensure the statements display the business name, sort code, account number and the account balance.',
];

type TProps = {
  isActive: boolean;
  onRequestUpload: () => void;
  showDirectUpload?: boolean;
};

const BankStatementUploadRevamp = ({
  isActive,
  onRequestUpload,
  showDirectUpload = false,
}: TProps) => {
  const stateKey = useContextStateKey();
  const { documents } = useGetDocuments(stateKey);

  const {
    uploadedFiles,
    setAllFiles,
    setFileLoading,
    setFileSuccess,
    setFileError,
    setFileReject,
    removeFile,
  } = useFileUploadReducer();

  const mutation = useDocumentUploadMutation();

  const handleUpload = (stateKey: string, body: FormData) => {
    return mutation.mutateAsync({ stateKey, body });
  };

  useEffect(() => {
    if (documents) {
      const bankStatementDocuments = documents.data.filter(
        (document) => document.attributes.document_type === DOCUMENT_TYPE,
      );
      setAllFiles(bankStatementDocuments);
    }
  }, [documents]);

  const hasUploadedOrLoadingFiles = uploadedFiles.length > 0;
  const showAccordion =
    !showDirectUpload && (isActive || !hasUploadedOrLoadingFiles);

  return (
    <div className={styles.container}>
      {showAccordion && (
        <Accordion
          title="If you prefer, you can upload your latest 6 months of bank statements (PDF)"
          small
          variant="secondary"
          data-testid="bank-statement-upload-accordion"
          titleClassName={styles.accordionHeader}
        >
          <DragDropUpload
            documentType={DOCUMENT_TYPE}
            listOfInstructions={listOfInstructions}
            onUpload={handleUpload}
            setFileLoading={setFileLoading}
            setFileSuccess={setFileSuccess}
            setFileError={setFileError}
            setFileReject={setFileReject}
          />
        </Accordion>
      )}

      {isActive && showDirectUpload && (
        <DragDropUpload
          documentType={DOCUMENT_TYPE}
          listOfInstructions={listOfInstructions}
          onUpload={handleUpload}
          setFileLoading={setFileLoading}
          setFileSuccess={setFileSuccess}
          setFileError={setFileError}
          setFileReject={setFileReject}
        />
      )}

      <FileStatusSections files={uploadedFiles} removeFile={removeFile} />

      {!isActive && hasUploadedOrLoadingFiles && (
        <button
          type="button"
          className={styles.uploadMoreButton}
          onClick={onRequestUpload}
        >
          Upload further documents
          <IconAngleRight className={styles.icon} />
        </button>
      )}
    </div>
  );
};

export default BankStatementUploadRevamp;
